import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { APP_LOCATION } from '@core/config/app-location';
import { AppSurfaceService } from '@core/config/app-surface.service';
import { SessionService } from '@core/config/session';
import { V2_ICONS } from '@v2/v2.icons';
import { V2_BRIEFING_SECTIONS } from '../domain/briefing-sections';

/**
 * The trip desk, as this repo already addresses it.
 *
 * `opscenter@flyevo.com` is the 24/7 Ops Center address the rail and the old
 * shell both link to. The mockup writes a placeholder; using the real one is
 * the difference between a dialog that ends somewhere and a dialog that ends.
 */
export const V2_BRIEFING_DESK_EMAIL = 'opscenter@flyevo.com';
export const V2_BRIEFING_DESK_MAILTO =
  `mailto:${V2_BRIEFING_DESK_EMAIL}?subject=Briefing%20access`;

/**
 * What each of the two viewers who can reach this dialog is actually told.
 *
 * There are now two of them (data-gaps G12 is closed: briefing routes are
 * reachable signed out):
 *
 *   - signed in, without `rights_briefing` -- telling them "Sign in" is
 *     nonsense (they are) and "Request an account" is worse (they have one).
 *     This branch is unchanged from when this dialog had only one voice.
 *   - signed out, no session at all -- for this viewer "Sign in" and
 *     "Request an account" are exactly the two ways in that exist, because
 *     one of them may already hold an account and one of them does not.
 *
 * Both branches still name the sections that are affected, name the two that
 * are not, and point at the desk that can change it. The section names come
 * from `V2_BRIEFING_SECTIONS` rather than being typed out, so moving a
 * section across the wall cannot leave this dialog describing the old
 * arrangement -- and the partition itself does not change with the viewer.
 */
@Component({
  selector: 'app-v2-briefing-access-dialog',
  templateUrl: './v2-briefing-access-dialog.component.html',
  styleUrls: ['./v2-briefing-access-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIcon],
  providers: [provideIcons(V2_ICONS)],
})
export class V2BriefingAccessDialogComponent {
  protected readonly activeModal = inject(NgbActiveModal);

  private readonly session = inject(SessionService);
  private readonly surface = inject(AppSurfaceService);
  private readonly location = inject(APP_LOCATION);

  protected readonly gatedSections = V2_BRIEFING_SECTIONS
    .filter(section => section.gated)
    .map(section => section.label);

  protected readonly openSections = V2_BRIEFING_SECTIONS
    .filter(section => !section.gated)
    .map(section => section.label);

  protected readonly deskEmail = V2_BRIEFING_DESK_EMAIL;
  protected readonly deskHref = V2_BRIEFING_DESK_MAILTO;

  /**
   * Whether this dialog is being read by a visitor with no session at all,
   * rather than a signed-in account that just lacks `rights_briefing`.
   *
   * Read from `SessionService` rather than taken as an `@Input`. This dialog
   * is opened imperatively via `NgbModal` from two places --
   * `v2-briefing-start` and `v2-briefing-page` -- and both are owned by other
   * work in flight on this branch; this task cannot touch them. An `@Input`
   * nobody sets defaults to signed-in and the signed-out branch never
   * activates -- correct code that never runs is worse than not writing it,
   * because the tests would still pass. Reading the session here means the
   * branch is live the moment this file ships, with no opener to update.
   */
  protected readonly signedOut = !this.session.hasSession();

  /**
   * Where "Sign in" sends a signed-out visitor: the estimator origin's
   * `/login`, with a `next` back to this exact page. Same shape as
   * `AuthGuard.sendToLogin`'s cross-origin branch (`back` is
   * `${origin}${path}`, then `surface.urlFor('estimator', ...)`), because
   * that is the return address Zitadel and `LoginWrapperComponent` already
   * know how to honour.
   */
  protected readonly loginHref = this.buildLoginHref();

  private buildLoginHref(): string {
    const path = `${this.location?.pathname ?? ''}${this.location?.search ?? ''}`;
    const back = `${this.location?.origin ?? ''}${path}`;
    return this.surface.urlFor('estimator', `/login?next=${encodeURIComponent(back)}`);
  }
}
